import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Space,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import { SinhVien } from '../../../../shared/types/database';
import { CreateSinhVienRequest, UpdateSinhVienRequest } from '../../../../shared/types/api';
import sinhVienService from '../../services/sinhVienService';
import { MoodleService } from '../../services/moodleService';

const { Title } = Typography;
const { Option } = Select;
const { Search } = Input;

// Interface for class options
interface ClassOption {
  LopHocID: string;
  TenLop: string;
  KhoaHocID: string;
  TenKhoaHoc: string;
}

const SinhVienManagementPage: React.FC = () => {
  const [students, setStudents] = useState<SinhVien[]>([]);
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editingStudent, setEditingStudent] = useState<SinhVien | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [form] = Form.useForm();

  const [moodleModalVisible, setMoodleModalVisible] = useState<boolean>(false);
  const [moodleSearchTerm, setMoodleSearchTerm] = useState<string>('');
  const [moodleUsers, setMoodleUsers] = useState<any[]>([]);
  const [moodleLoading, setMoodleLoading] = useState<boolean>(false);

  const [passwordModalVisible, setPasswordModalVisible] = useState<boolean>(false);
  const [selectedMoodleUser, setSelectedMoodleUser] = useState<any | null>(null);
  const [newPassword, setNewPassword] = useState<string>('');

  const moodleService = new MoodleService(); // Create instance

  // Load classes on component mount
  useEffect(() => {
    loadClasses();
  }, []);

  // Load students when class selection changes
  useEffect(() => {
    if (selectedClass) {
      loadStudents();
    } else {
      setStudents([]);
      setTotalItems(0);
    }
  }, [selectedClass, currentPage, pageSize, searchTerm]);

  // Load list of classes from API
  const loadClasses = async (): Promise<void> => {
    try {
      setLoading(true);

      // Use simple fetch with large limit to get all classes
      const response = await fetch('/api/v1/classes?limit=1000');

      const result = await response.json();

      if (result.success && result.data) {
        const classOptions = result.data.map((cls: any) => ({
          LopHocID: cls.LopHocID,
          TenLop: cls.TenLop,
          KhoaHocID: cls.KhoaHocID,
          TenKhoaHoc: cls.TenKhoaHoc || 'Chưa xác định'
        }));
        setClasses(classOptions);

        if (classOptions.length === 0) {
          message.info('Không tìm thấy lớp học nào');
        }
      } else {
        message.error(result.error || 'Không thể tải danh sách lớp học');
      }
    } catch (error) {
      console.error('Error loading classes:', error);
      message.error('Lỗi khi tải danh sách lớp học');
    } finally {
      setLoading(false);
    }
  };

  // Load students for selected class
  const loadStudents = async (): Promise<void> => {
    if (!selectedClass) return;

    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: pageSize,
        lopHocId: selectedClass,
        search: searchTerm || undefined
      };

      const response = await sinhVienService.getList(params);

      if (response.success) {
        setStudents(response.data);
        setTotalItems(response.pagination.totalItems);
      } else {
        message.error(response.error || 'Không thể tải danh sách sinh viên');
      }
    } catch (error) {
      console.error('Error loading students:', error);
      message.error('Lỗi khi tải danh sách sinh viên');
    } finally {
      setLoading(false);
    }
  };

  // Handle class selection change
  const handleClassChange = (value: string): void => {
    setSelectedClass(value);
    setCurrentPage(1); // Reset to first page
    setSearchTerm(''); // Clear search
  };

  // Handle create new student
  const handleCreate = (): void => {
    if (!selectedClass) {
      message.warning('Vui lòng chọn lớp học trước');
      return;
    }
    setEditingStudent(null);
    form.resetFields();
    form.setFieldsValue({ MaLop: selectedClass }); // Pre-fill class
    setModalVisible(true);
  };

  // Handle edit student
  const handleEdit = (student: SinhVien): void => {
    setEditingStudent(student);
    const formData = {
      ...student,
      NgaySinh: student.Ngaysinh ? dayjs(student.Ngaysinh) : null,
      MaLop: selectedClass
    };
    form.setFieldsValue(formData);
    setModalVisible(true);
  };

  // Handle delete student
  const handleDelete = async (studentId: string): Promise<void> => {
    try {
      const response = await sinhVienService.delete(studentId);
      if (response.success) {
        message.success('Xóa sinh viên thành công');
        loadStudents();
      } else {
        message.error(response.error || 'Không thể xóa sinh viên');
      }
    } catch (error) {
      console.error('Error deleting student:', error);
      message.error('Lỗi khi xóa sinh viên');
    }
  };

  // Handle sync students for selected class from secondary database
  const handleSync = async (safeMode: boolean = true): Promise<void> => {
    if (!selectedClass) {
      message.warning('Vui lòng chọn lớp học cần đồng bộ');
      return;
    }

    const syncType = safeMode ? 'an toàn (không xóa)' : 'toàn diện (có xóa)';

    try {
      message.loading(`Đang đồng bộ ${syncType} dữ liệu sinh viên từ hệ thống cũ...`, 0);
      const response = await sinhVienService.sync(selectedClass, safeMode);
      message.destroy();

      if (response.success) {
        const { inserted = 0, updated = 0, deleted = 0, preserved = 0, invalidStudents = 0 } = response.data || {};

        let successMsg = `Đồng bộ ${syncType} thành công!\n`;
        successMsg += `• ${inserted} sinh viên mới\n`;
        successMsg += `• ${updated} sinh viên được cập nhật`;

        if (safeMode && preserved > 0) {
          successMsg += `\n• ${preserved} sinh viên được bảo lưu (có trong CSDL chính nhưng không có trong CSDL phụ)`;
        }
        if (!safeMode && deleted > 0) {
          successMsg += `\n• ${deleted} sinh viên đã xóa`;
        }
        if (invalidStudents > 0) {
          successMsg += `\n⚠️ ${invalidStudents} sinh viên có dữ liệu không hợp lệ bị bỏ qua`;
        }

        message.success(successMsg);
        loadStudents();
      } else {
        message.error(response.error || 'Không thể đồng bộ dữ liệu');
      }
    } catch (error) {
      message.destroy();
      console.error('Error syncing students:', error);
      message.error('Lỗi khi đồng bộ dữ liệu sinh viên');
    }
  };

  // Handle form submit
  const handleSubmit = async (): Promise<void> => {
    try {
      const values = await form.validateFields();
      const formData = {
        ...values,
        NgaySinh: values.NgaySinh ? values.NgaySinh.format('YYYY-MM-DD') : undefined,
        MaLop: selectedClass // Ensure using selected class
      };

      let response;
      if (editingStudent) {
        response = await sinhVienService.update(editingStudent.MaHS, formData);
      } else {
        response = await sinhVienService.create(formData);
      }

      if (response.success) {
        message.success(editingStudent ? 'Cập nhật sinh viên thành công' : 'Thêm sinh viên thành công');
        setModalVisible(false);
        form.resetFields();
        setEditingStudent(null);
        loadStudents();
      } else {
        message.error(response.error || 'Có lỗi xảy ra');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      message.error('Lỗi khi lưu thông tin sinh viên');
    }
  };

  // Handle Moodle User Search
  const handleMoodleSearch = async () => {
    if (!moodleSearchTerm) {
      message.warning('Vui lòng nhập từ khóa tìm kiếm');
      return;
    }
    setMoodleLoading(true);
    try {
      const response = await moodleService.searchUsers(moodleSearchTerm);
      if (response.success) {
        setMoodleUsers(response.data || []);
      } else {
        message.error(response.error || 'Không thể tìm kiếm user Moodle');
      }
    } catch (error) {
      message.error('Lỗi khi tìm kiếm user Moodle');
    }
    setMoodleLoading(false);
  };

  // Handle Create Moodle Users for Class
  const handleCreateMoodleUsers = async () => {
    if (!selectedClass) {
      message.warning('Vui lòng chọn lớp học');
      return;
    }
    setLoading(true);
    try {
      const response = await moodleService.createUsersForClass(selectedClass);
      if (response.success) {
        message.success(response.message || 'Đã tạo user Moodle thành công');
      } else {
        message.error(response.error || 'Không thể tạo user Moodle');
      }
    } catch (error) {
      message.error('Lỗi khi tạo user Moodle');
    }
    setLoading(false);
  };

  // Handle Create Moodle Users for ThiThu Class
  const handleCreateMoodleUsersThiThu = async () => {
    if (!selectedClass) {
      message.warning('Vui lòng chọn lớp học');
      return;
    }
    setLoading(true);
    try {
      const response = await moodleService.createUsersForClassThiThu(selectedClass);
      if (response.success) {
        message.success(response.message || 'Đã tạo user Moodle Thi Thử thành công');
      } else {
        message.error(response.error || 'Không thể tạo user Moodle Thi Thử');
      }
    } catch (error) {
      message.error('Lỗi khi tạo user Moodle Thi Thử');
    }
    setLoading(false);
  };

  // Handle opening the change password modal
  const handleOpenPasswordModal = (user: any) => {
    setSelectedMoodleUser(user);
    setNewPassword('');
    setPasswordModalVisible(true);
  };

  // Handle changing the password
  const handlePasswordChange = async () => {
    if (!selectedMoodleUser || !newPassword) {
      message.warning('Vui lòng nhập mật khẩu mới');
      return;
    }
    setMoodleLoading(true);
    try {
      const response = await moodleService.updateUserPassword(selectedMoodleUser.id, newPassword);
      if (response.success) {
        message.success('Đổi mật khẩu thành công!');
        setPasswordModalVisible(false);
      } else {
        message.error(response.error || 'Không thể đổi mật khẩu');
      }
    } catch (error) {
      message.error('Lỗi khi đổi mật khẩu');
    }
    setMoodleLoading(false);
  };

  // Table columns definition
  const columns: ColumnsType<SinhVien> = [
    {
      title: 'Mã HS',
      dataIndex: 'MaHS',
      key: 'MaHS',
      width: 120,
      fixed: 'left'
    },
    {
      title: 'Họ tên',
      dataIndex: 'Hoten',
      key: 'Hoten',
      width: 200,
      render: (text: string) => <strong>{text}</strong>
    },
    {
      title: 'Ngày sinh',
      dataIndex: 'Ngaysinh',
      key: 'Ngaysinh',
      width: 120,
      render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY') : ''
    },
    {
      title: 'Giới tính',
      dataIndex: 'Gioitinh',
      key: 'Gioitinh',
      width: 100
    },
    {
      title: 'Nơi sinh',
      dataIndex: 'NoiSinh',
      key: 'NoiSinh',
      width: 150
    },
    {
      title: 'Lớp',
      dataIndex: 'TenLop',
      key: 'TenLop',
      width: 120
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Popconfirm
            title="Xác nhận xóa"
            description="Bạn có chắc chắn muốn xóa sinh viên này?"
            onConfirm={() => handleDelete(record.MaHS)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Get selected class info for display
  const selectedClassInfo = classes.find(cls => cls.LopHocID === selectedClass);

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              <UserOutlined /> Quản lý Sinh viên
            </Title>
          </Col>
        </Row>

        {/* Class Selection */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col span={12}>
            <div style={{ marginBottom: '8px' }}>
              <strong>Chọn lớp học:</strong>
            </div>
            <Select
              style={{ width: '100%' }}
              placeholder="Chọn lớp học để xem danh sách sinh viên"
              value={selectedClass}
              onChange={handleClassChange}
              loading={loading}
              showSearch
              filterOption={(input, option) =>
                option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
              }
            >
              {classes.map(cls => (
                <Option key={cls.LopHocID} value={cls.LopHocID}>
                  {cls.TenLop} - {cls.TenKhoaHoc}
                </Option>
              ))}
            </Select>
          </Col>

          {selectedClass && (
            <Col span={12}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Tìm kiếm sinh viên:</strong>
              </div>
              <Search
                placeholder="Tìm kiếm theo tên hoặc mã sinh viên..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onSearch={loadStudents}
                enterButton={<SearchOutlined />}
              />
            </Col>
          )}
        </Row>

        {/* Selected Class Info */}
        {selectedClassInfo && (
          <div style={{ marginBottom: '16px' }}>
            <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
              Lớp: {selectedClassInfo.TenLop} | Khóa: {selectedClassInfo.TenKhoaHoc} | Sinh viên: {totalItems}
            </Tag>
          </div>
        )}

        {/* Controls */}
        {selectedClass && (
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col span={24}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '16px',
                alignItems: 'center',
                padding: '16px',
                backgroundColor: '#fafafa',
                borderRadius: '6px',
                border: '1px solid #d9d9d9'
              }}>
                {/* Left side buttons */}
                <Space wrap>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={loadStudents}
                    type="default"
                  >
                    Tải lại
                  </Button>

                  <Button
                    type="default"
                    style={{
                      backgroundColor: '#52c41a',
                      borderColor: '#52c41a',
                      color: 'white'
                    }}
                    onClick={() => handleSync(true)}
                  >
                    🔄 Đồng bộ an toàn
                  </Button>

                  <Button
                    type="default"
                    style={{
                      backgroundColor: '#ff7875',
                      borderColor: '#ff7875',
                      color: 'white'
                    }}
                    onClick={() => handleSync(false)}
                  >
                    ⚠️ Đồng bộ toàn diện
                  </Button>
                </Space>

                {/* Right side buttons */}
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                  >
                    Thêm sinh viên
                  </Button>

                  <Button
                    type="default"
                    style={{ color: '#1890ff' }}
                  >
                    📤 Import Excel
                  </Button>

                  <Button
                    type="default"
                    style={{ color: '#1890ff' }}
                  >
                    📄 Tải file mẫu
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => setMoodleModalVisible(true)}
                  >
                    Tìm User Moodle
                  </Button>
                  <Button
                    type="default"
                    onClick={handleCreateMoodleUsers}
                  >
                    Tạo user Moodle cho lớp
                  </Button>
                  <Button
                    type="default"
                    danger
                    onClick={handleCreateMoodleUsersThiThu}
                    style={{ borderColor: '#fa8c16', color: '#fa8c16' }}
                  >
                    Tạo user Thi Thử cho lớp
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        )}

        <Divider />

        {/* Instructions when no class selected */}
        {!selectedClass && (
          <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
            <UserOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
            <p style={{ fontSize: '16px' }}>Vui lòng chọn lớp học để xem danh sách sinh viên</p>
          </div>
        )}

        {/* Table */}
        {selectedClass && (
          <Table
            columns={columns}
            dataSource={students}
            rowKey="MaHS"
            loading={loading}
            pagination={{
              current: currentPage,
              total: totalItems,
              pageSize: pageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} của ${total} sinh viên`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size || pageSize);
              },
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            scroll={{ x: 1000 }}
            size="small"
            bordered
          />
        )}
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingStudent ? 'Sửa thông tin sinh viên' : 'Thêm sinh viên mới'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
        okText={editingStudent ? 'Cập nhật' : 'Thêm'}
        cancelText="Hủy"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="MaHS"
            label="Mã học sinh"
            rules={[{ required: true, message: 'Vui lòng nhập mã học sinh' }]}
          >
            <Input disabled={!!editingStudent} />
          </Form.Item>

          <Form.Item
            name="Hoten"
            label="Họ tên"
            rules={[{ required: true, message: 'Vui lòng nhập họ tên' }]}
          >
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="NgaySinh" label="Ngày sinh">
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="Chọn ngày sinh"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="GioiTinh" label="Giới tính">
                <Select placeholder="Chọn giới tính">
                  <Option value="Nam">Nam</Option>
                  <Option value="Nữ">Nữ</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="NoiSinh" label="Nơi sinh">
            <Input placeholder="Ví dụ: Hà Nội" />
          </Form.Item>

          <Form.Item
            name="MaLop"
            label="Mã lớp"
          >
            <Input disabled value={selectedClass} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Moodle Search Modal */}
      <Modal
        title="Tìm kiếm User Moodle"
        open={moodleModalVisible}
        onCancel={() => setMoodleModalVisible(false)}
        footer={null}
        width={800}
      >
        <Input.Search
          placeholder="Nhập tên, username, hoặc email..."
          value={moodleSearchTerm}
          onChange={(e) => setMoodleSearchTerm(e.target.value)}
          onSearch={handleMoodleSearch}
          enterButton="Tìm kiếm"
          loading={moodleLoading}
        />
        <Table
          style={{ marginTop: '16px' }}
          columns={[
            { title: 'ID', dataIndex: 'id', key: 'id' },
            { title: 'Họ tên', dataIndex: 'fullname', key: 'fullname' },
            { title: 'Email', dataIndex: 'email', key: 'email' },
            { title: 'Khoa/Phòng', dataIndex: 'department', key: 'department' },
            {
              title: 'Thao tác',
              key: 'action',
              render: (_, record) => (
                <Button type="link" onClick={() => handleOpenPasswordModal(record)}>
                  Đổi mật khẩu
                </Button>
              ),
            },
          ]}
          dataSource={moodleUsers}
          loading={moodleLoading}
          rowKey="id"
          pagination={false}
        />
      </Modal>

      {/* Change Password Modal */}
      <Modal
        title={`Đổi mật khẩu cho ${selectedMoodleUser?.fullname}`}
        open={passwordModalVisible}
        onOk={handlePasswordChange}
        onCancel={() => setPasswordModalVisible(false)}
        confirmLoading={moodleLoading}
      >
        <Input.Password
          placeholder="Nhập mật khẩu mới"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default SinhVienManagementPage; 